import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {
  Chart,
  ChartConfiguration,
  ChartDataset,
  ChartType,
  TooltipItem,
} from 'chart.js/auto';

interface EvolutionSeries {
  label: string;
  data: Array<number | null>;
  color?: string;
  highlight?: boolean;
}

@Component({
  selector: 'app-standings-evolution-chart',
  templateUrl: './standings-evolution-chart.component.html',
  styleUrls: ['./standings-evolution-chart.component.scss'],
  standalone: false,
})
export class StandingsEvolutionChartComponent
  implements OnChanges, OnDestroy
{
  @Input() title: string = 'Evolucion de la clasificacion';
  @Input() subtitle: string = '';
  @Input() labels: string[] = [];
  @Input() series: EvolutionSeries[] = [];
  @Input() yAxisMode: 'position' | 'points' = 'position';
  @Input() groups: Array<{ _id: string; name: string }> = [];
  @Input() selectedGroup: string = 'general';
  @Input() showLegend: boolean = true;
  @Output() selectedGroupChange = new EventEmitter<string>();

  @ViewChild('chartCanvas')
  set chartCanvas(value: ElementRef<HTMLCanvasElement> | undefined) {
    this.chartCanvasRef = value;
    if (value) {
      this.renderChart();
    }
  }

  private chart?: Chart;
  private chartCanvasRef?: ElementRef<HTMLCanvasElement>;

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['labels'] ||
      changes['series'] ||
      changes['yAxisMode'] ||
      changes['showLegend']
    ) {
      this.renderChart();
    }
  }

  ngOnDestroy(): void {
    this.chart?.destroy();
  }

  hasData(): boolean {
    return this.series.some((item) =>
      item.data?.some((value) => typeof value === 'number' && value >= 0),
    );
  }

  get currentValue(): number | null {
    if (this.series.length !== 1) {
      return null;
    }

    const values = this.series[0].data.filter(
      (value): value is number => typeof value === 'number',
    );
    return values.at(-1) ?? null;
  }

  get currentValueLabel(): string {
    if (this.currentValue === null) {
      return '';
    }

    return this.yAxisMode === 'position'
      ? `${this.currentValue}ª`
      : `${this.currentValue} pts`;
  }

  private renderChart(): void {
    if (!this.chartCanvasRef?.nativeElement || !this.hasData()) {
      this.chart?.destroy();
      this.chart = undefined;
      return;
    }

    const chartLabels =
      this.labels.length > 0
        ? this.labels
        : this.series[0].data.map((_, idx) => `J${idx + 1}`);

    const canvas = this.chartCanvasRef.nativeElement;
    const datasets = this.series.map((item, index) =>
      this.buildDataset(item, index, canvas),
    );

    const config: ChartConfiguration = {
      type: 'line' as ChartType,
      data: {
        labels: chartLabels,
        datasets,
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        normalized: true,
        layout: {
          padding: {
            top: 10,
            right: 8,
            bottom: 2,
            left: 2,
          },
        },
        animation: {
          duration: 700,
          easing: 'easeOutQuart',
        },
        interaction: {
          mode: 'index',
          intersect: false,
        },
        plugins: {
          legend: {
            display: this.showLegend,
            labels: {
              usePointStyle: true,
              pointStyle: 'circle',
              boxHeight: 7,
              boxWidth: 7,
              color: '#5b6880',
              padding: 16,
              font: {
                size: 11,
                weight: 600,
              },
            },
          },
          tooltip: {
            backgroundColor: 'rgba(15, 31, 59, 0.96)',
            titleColor: '#ffffff',
            bodyColor: '#ffffff',
            padding: 12,
            cornerRadius: 10,
            caretSize: 6,
            displayColors: this.series.length > 1,
            titleFont: {
              size: 11,
              weight: 600,
            },
            bodyFont: {
              size: 12,
              weight: 700,
            },
            callbacks: {
              title: (items) => items[0]?.label || '',
              label: (ctx: TooltipItem<'line'>) => {
                const value =
                  this.yAxisMode === 'position'
                    ? `${ctx.parsed.y}ª posición`
                    : `${ctx.parsed.y} puntos`;
                return this.series.length > 1
                  ? `${ctx.dataset.label}: ${value}`
                  : value;
              },
            },
          },
        },
        scales: {
          x: {
            border: {
              display: false,
            },
            grid: {
              display: false,
            },
            ticks: {
              color: '#7b879c',
              maxRotation: 0,
              padding: 8,
              font: {
                size: 10,
                weight: 600,
              },
            },
          },
          y: {
            beginAtZero: this.yAxisMode === 'points',
            reverse: this.yAxisMode === 'position',
            min: this.yAxisMode === 'position' ? 1 : undefined,
            border: {
              display: false,
            },
            ticks: {
              stepSize: 1,
              color: '#7b879c',
              padding: 8,
              font: {
                size: 10,
                weight: 600,
              },
              callback: (value) =>
                this.yAxisMode === 'position' ? `${value}ª` : value,
            },
            grid: {
              color: 'rgba(91, 104, 128, 0.12)',
              drawTicks: false,
            },
          },
        },
      },
    };

    if (this.chart) {
      this.chart.data = config.data;
      this.chart.options = config.options || {};
      this.chart.update();
      return;
    }

    this.chart = new Chart(this.chartCanvasRef.nativeElement, config);
  }

  private buildDataset(
    item: EvolutionSeries,
    index: number,
    canvas: HTMLCanvasElement,
  ): ChartDataset<'line'> {
    const fallbackPalette = [
      '#304ffe',
      '#ef4444',
      '#10b981',
      '#f59e0b',
      '#7e6dd8',
    ];
    const strokeColor =
      item.color || fallbackPalette[index % fallbackPalette.length];
    const isSingleSeries = this.series.length === 1;
    const lineGradient = canvas
      .getContext('2d')
      ?.createLinearGradient(0, 0, canvas.clientWidth || 320, 0);
    const areaGradient = canvas
      .getContext('2d')
      ?.createLinearGradient(0, 0, 0, canvas.clientHeight || 220);

    if (isSingleSeries && lineGradient && areaGradient) {
      lineGradient.addColorStop(0, '#00b8d9');
      lineGradient.addColorStop(0.5, '#304ffe');
      lineGradient.addColorStop(1, '#7e57c2');
      areaGradient.addColorStop(0, 'rgba(48, 79, 254, 0.24)');
      areaGradient.addColorStop(0.65, 'rgba(48, 79, 254, 0.06)');
      areaGradient.addColorStop(1, 'rgba(48, 79, 254, 0)');
    }

    return {
      label: item.label,
      data: item.data,
      borderColor: isSingleSeries && lineGradient ? lineGradient : strokeColor,
      backgroundColor:
        isSingleSeries && areaGradient ? areaGradient : strokeColor,
      borderWidth: isSingleSeries ? 3.5 : item.highlight ? 3 : 2,
      borderCapStyle: 'round',
      borderJoinStyle: 'round',
      pointRadius: isSingleSeries ? 4 : item.highlight ? 3 : 2,
      pointHoverRadius: isSingleSeries ? 7 : 5,
      pointHitRadius: 18,
      pointBackgroundColor: '#ffffff',
      pointBorderColor: isSingleSeries ? '#304ffe' : strokeColor,
      pointBorderWidth: isSingleSeries ? 2.5 : 2,
      pointHoverBackgroundColor: '#ffffff',
      pointHoverBorderWidth: 3,
      fill: isSingleSeries ? 'origin' : false,
      tension: 0.38,
      spanGaps: true,
    };
  }
}
